/**
 * useSyncQueue — Offline sync queue management.
 *
 * Responsibilities:
 * - Exposes `pendingCount` from the module-level syncQueueStore
 * - Monitors online + auth state
 * - Drains the queue when the device comes back online:
 *     • For each queued save: fetches the Firestore version, compares with
 *       mergeTask(), pushes local if local wins, logs conflict if same-version
 *     • For each queued delete: deletes from Firestore
 * - Conflicts are stored via conflictService and callers are notified
 *
 * Returns:
 *   { pendingCount, isProcessing, drainQueue }
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../services/firebase';
import {
  saveTask,
  saveAgendaEvent,
  deleteTask as deleteTaskFirestore,
  deleteAgendaEvent as deleteAgendaEventFirestore,
  mergeTask,
  getTaskDoc,
  getAgendaEventDoc,
  isAuthenticated,
} from '../services/firestoreSync';
import {
  getQueue,
  dequeue,
  subscribeToQueue,
} from '../services/syncQueueStore';
import {
  addConflict,
} from '../services/conflictService';
import { captureError, SEVERITY } from '../services/errorService';

export function useSyncQueue() {
  const [pendingCount, setPendingCount] = useState(() => getQueue().length);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const processingRef = useRef(false);

  // Reflect queue-store changes into React state
  useEffect(() => {
    const unsub = subscribeToQueue(queue => setPendingCount(queue.length));
    return unsub;
  }, []);

  // Monitor online/offline transitions
  useEffect(() => {
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  /**
   * Process all pending operations in chronological order.
   * Safe to call multiple times — uses a ref to prevent concurrent runs.
   */
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    if (!navigator.onLine || !isAuthenticated()) return;

    const queue = getQueue();
    if (queue.length === 0) return;

    processingRef.current = true;
    setIsProcessing(true);

    for (const op of queue) {
      try {
        if (op.type === 'delete') {
          // ── Delete ─────────────────────────────────────────────────────────
          if (op.entityType === 'task') {
            await deleteTaskFirestore(op.entityId);
          } else {
            await deleteAgendaEventFirestore(op.entityId);
          }
          dequeue(op.id);

        } else if (op.type === 'save' && op.data) {
          // ── Save with version-aware merge ──────────────────────────────────
          const fetchDoc = op.entityType === 'task' ? getTaskDoc : getAgendaEventDoc;
          const remoteDoc = await fetchDoc(op.data.id);

          if (!remoteDoc) {
            // Document doesn't exist in Firestore yet — safe to push
            await (op.entityType === 'task' ? saveTask : saveAgendaEvent)(op.data);
            dequeue(op.id);
            continue;
          }

          const { result } = mergeTask(op.data, remoteDoc);

          if (result === 'push' || result === 'equal') {
            await (op.entityType === 'task' ? saveTask : saveAgendaEvent)(op.data);
            dequeue(op.id);
          } else if (result === 'pull') {
            // Cloud is newer — the onSnapshot listener will update local state
            dequeue(op.id);
          } else if (result === 'conflict') {
            // Same version but different content — store for user resolution
            addConflict(op.data.id, op.data, remoteDoc, op.entityType);
            dequeue(op.id);
          }
        }
      } catch (error) {
        captureError(error, `drain op ${op.type}/${op.entityType}/${op.entityId}`, SEVERITY.BACKGROUND);
        // Leave the op in the queue so it is retried next time
      }
    }

    processingRef.current = false;
    setIsProcessing(false);
  }, []);

  // Auto-drain when coming back online
  useEffect(() => {
    if (isOnline) {
      drainQueue();
    }
  }, [isOnline, drainQueue]);

  // Also drain when auth becomes available
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      if (user && navigator.onLine) {
        drainQueue();
      }
    });
    return unsub;
  }, [drainQueue]);

  return { pendingCount, isProcessing, drainQueue };
}
